import { useState, useEffect } from 'react';
import {
  ExercisesContainer,
  RoutinesContainer,
  APP_CONFIG,
  AppProvider,
  ErrorBoundary,
} from './domains';
import {
  AuthProvider,
  useAuth,
  LoginPage,
  CompleteProfilePage,
} from './domains/auth';
import DashboardContainer from './domains/dashboard/components/DashboardContainer';
import AdminUsersContainer from './domains/admin/components/AdminUsersContainer';
import Header from './domains/shared/components/Header';
import ClassesContainer from './domains/classes/components/ClassesContainer';
import TicketsContainer from './domains/tickets/components/TicketsContainer';
import WeeklyPlanContainer from './domains/routines/components/WeeklyPlanContainer';
import {
  HomeIcon,
  ClipboardIcon,
  DumbbellIcon,
  CrownIcon,
  RepeatIcon,
  BellIcon,
  CalendarIcon,
} from './assets/icons/index.tsx';

// ─── Tipos de página ────────────────────────────────────────────────────────

type Page =
  | 'login'
  | 'dashboard'
  | 'exercises'
  | 'routines'
  | 'classes'
  | 'tickets'
  | 'monitor'
  | 'admin';

// ─── Contenido principal (requiere auth) ─────────────────────────────────────

function AppContent() {
  const { isAuthenticated, isLoading, isAdmin, isInstructor, user } =
    useAuth();
  // Un admin tambien es monitor a efectos del panel; `isInstructor` es
  // igualdad estricta con el rol, asi que hay que sumarlos a mano.
  const puedeEditarElPlan = isInstructor || isAdmin;
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Redirigir a login si no está autenticado
  useEffect(() => {
    if (!isLoading && !isAuthenticated && currentPage !== 'login') {
      setCurrentPage('login');
    } else if (!isLoading && isAuthenticated && currentPage === 'login') {
      setCurrentPage('dashboard');
    }
  }, [isAuthenticated, isLoading, currentPage]);

  const navigate = (page: string) => {
    // Proteger la ruta admin
    if (page === 'admin' && !isAdmin) return;
    if (page === 'monitor' && !puedeEditarElPlan) return;
    setCurrentPage(page as Page);
  };

  // Pantalla de carga inicial
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f172a]">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full border-2 border-[#334155] border-t-[#1f9e3f] animate-spin" />
          <p className="text-[#94a3b8] text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  // Página de acceso (sin header ni navegación)
  if (currentPage === 'login' || !isAuthenticated) {
    return <LoginPage />;
  }

  // Primer acceso: con OTP no hay formulario de registro, así que pedimos el
  // nombre aquí. Si el perfil no cargó (backend caído) no bloqueamos la app.
  if (user?.profile && !user.profile.fullName) {
    return <CompleteProfilePage />;
  }

  // Tabs de navegación según rol
  const navigationTabs = [
    { id: 'dashboard' as const, label: 'Dashboard', Icon: HomeIcon },
    { id: 'exercises' as const, label: 'Ejercicios', Icon: ClipboardIcon },
    { id: 'routines' as const, label: 'Rutinas', Icon: DumbbellIcon },
    { id: 'classes' as const, label: 'Clases', Icon: RepeatIcon },
    { id: 'tickets' as const, label: 'Tickets', Icon: BellIcon },
    ...(puedeEditarElPlan
      ? [{ id: 'monitor' as const, label: 'Monitor', Icon: CalendarIcon }]
      : []),
    ...(isAdmin ? [{ id: 'admin' as const, label: 'Admin', Icon: CrownIcon }] : []),
  ];

  const renderContent = () => {
    switch (currentPage) {
      case 'dashboard':
        return (
          <DashboardContainer
            onNavigate={(page) => setCurrentPage(page)}
          />
        );
      case 'exercises':
        return <ExercisesContainer />;
      case 'routines':
        return <RoutinesContainer />;
      case 'classes':
        return <ClassesContainer />;
      case 'tickets':
        return <TicketsContainer />;
      case 'monitor':
        return puedeEditarElPlan ? <WeeklyPlanContainer /> : null;
      case 'admin':
        return isAdmin ? <AdminUsersContainer /> : null;
      default:
        return (
          <DashboardContainer
            onNavigate={(page) => setCurrentPage(page)}
          />
        );
    }
  };

  return (
    <AppProvider>
      <div
        className="min-h-screen"
        style={{ background: APP_CONFIG.THEME.BACKGROUND_STYLE }}
      >
        <Header onNavigate={navigate} />

        {/* Navegación secundaria */}
        <div
          className="sticky z-40 w-full bg-[#0d1422] border-b border-white/[0.06]"
          style={{ top: 'calc(4rem + env(safe-area-inset-top))' }}
        >
          <div className="container mx-auto px-3 sm:px-4">
            {/* Mobile: hamburguesa + nombre de sección */}
            <div className="sm:hidden flex items-center justify-between py-2">
              <button
                onClick={() => setDrawerOpen(true)}
                aria-label="Abrir menú"
                className="p-2 rounded-lg text-[#cbd5e1] hover:bg-white/5"
              >
                <svg width={22} height={22} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
                </svg>
              </button>
              <span className="text-white text-sm font-semibold">
                {navigationTabs.find((t) => t.id === currentPage)?.label ?? ''}
              </span>
              <span className="w-9" aria-hidden />
            </div>

            {/* Desktop: tabs */}
            <nav className="hidden sm:flex gap-1 py-2 overflow-x-auto">
              {navigationTabs.map(({ id, label, Icon }) => {
                const isActive = currentPage === id;
                return (
                  <button
                    key={id}
                    onClick={() => navigate(id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${isActive ? 'bg-[#1f9e3f] text-white' : 'bg-transparent text-[#94a3b8] hover:text-white hover:bg-white/5'}`}
                  >
                    <Icon size={18} />
                    <span>{label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Drawer lateral (mobile only) */}
        {drawerOpen && (
          <div className="fixed inset-0 z-[60] sm:hidden">
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => setDrawerOpen(false)}
            />
            <aside
              className="absolute left-0 top-0 bottom-0 w-72 max-w-[80vw] bg-[#0b1120] border-r border-white/10 shadow-2xl flex flex-col"
              style={{ paddingTop: 'env(safe-area-inset-top)' }}
            >
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <span className="text-white font-semibold">Menú</span>
                <button
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Cerrar menú"
                  className="text-[#94a3b8] hover:text-white text-xl leading-none"
                >
                  ✕
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto py-2">
                {navigationTabs.map(({ id, label, Icon }) => {
                  const isActive = currentPage === id;
                  return (
                    <button
                      key={id}
                      onClick={() => {
                        navigate(id);
                        setDrawerOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-[#1f9e3f] text-white'
                          : 'text-[#cbd5e1] hover:bg-white/5'
                      }`}
                    >
                      <Icon size={20} />
                      <span>{label}</span>
                    </button>
                  );
                })}
              </nav>
            </aside>
          </div>
        )}

        {/* Contenido
            La barrera va por dentro del layout, no por fuera: si una sección
            revienta, la cabecera y la navegación siguen ahí y se puede salir a
            otra. Al cambiar de sección se reintenta sola (resetKey). */}
        <main className="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
          <ErrorBoundary resetKey={currentPage}>
            {renderContent()}
          </ErrorBoundary>
        </main>
      </div>
    </AppProvider>
  );
}

// ─── App raíz ────────────────────────────────────────────────────────────────

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
